import { faro, LogLevel } from '@grafana/faro-web-sdk'

/**
 * Canonical event-name catalog. Every observability emission references
 * a constant here, NOT a raw string at the call site — same discipline
 * as the BE `App\Lib\Metrics\Instruments`.
 *
 * Naming: `<domain>.<event>[.<qualifier>]`. Dot separators, snake_case
 * within segments.
 */
export const FaroEvents = {
  // Auth lifecycle
  AUTH_LOGIN_ATTEMPT: 'auth.login.attempt',
  AUTH_LOGIN_SUCCESS: 'auth.login.success',
  AUTH_LOGIN_FAILED: 'auth.login.failed',
  AUTH_LOGOUT: 'auth.logout',
  AUTH_HYDRATE_SUCCESS: 'auth.hydrate.success',
  AUTH_HYDRATE_NO_SESSION: 'auth.hydrate.no_session',
  AUTH_SESSION_CLEARED: 'auth.session.cleared',
  AUTH_LOCKOUT: 'auth.lockout',
  AUTH_UNAUTHORIZED: 'auth.unauthorized',

  // HTTP lifecycle (useAPI)
  HTTP_REQUEST_START: 'http.request.start',
  HTTP_REQUEST_RETRY: 'http.request.retry',
  HTTP_REQUEST_FAILED: 'http.request.failed',
  HTTP_REQUEST_NETWORK_ERROR: 'http.request.network_error',
  HTTP_REQUEST_ABORTED: 'http.request.aborted',
  HTTP_CSRF_REFRESH: 'http.csrf.refresh',
  HTTP_CSRF_REFRESH_FAILED: 'http.csrf.refresh_failed',
} as const

export type FaroEventName = (typeof FaroEvents)[keyof typeof FaroEvents]

/**
 * Attribute shape for an event. Values are coerced to string at the
 * Faro boundary — keep them low-cardinality and PII-free:
 *
 *  - status code: '401' / '500' / '2xx' — OK
 *  - route or endpoint path: '/api/auth/login' — OK
 *  - retry attempt number: '1' / '2' / '3' — OK
 *  - error message KEY ('csrf_mismatch', 'network', 'throttled') — OK
 *
 *  - email / user_id / tenant_id / request body — NEVER. Faro tags the
 *    session_id and view_name automatically; that's the user-tracing layer.
 */
export type FaroAttributes = Record<string, string | number | boolean>

/**
 * Attribute argument shape — accepts either a literal dict OR a
 * closure that produces one. Closures are invoked ONLY after the
 * latch check passes — when observability is disabled, the prep
 * work inside the closure NEVER runs. Use the closure form when
 * attribute construction is non-trivial (object reads, string
 * concat, derived values).
 */
type AttrsArg = FaroAttributes | (() => FaroAttributes)

/**
 * ## Hard contracts (matches BE `App\Lib\Metrics\Metrics`)
 *
 * 1. **Latch — full skip when disabled.** `VITE_FARO_DISABLED=true`
 *    at build OR a runtime `setObservabilityEnabled(false)` makes
 *    every helper return IMMEDIATELY: closures NOT invoked, no
 *    attribute coercion, no Faro SDK call. Zero performance impact.
 *
 * 2. **Closure-arg form** for attribute building. Pass `() => ({
 *    ... })` so the data prep happens AFTER the latch check, not
 *    before. High-frequency call sites (HTTP middleware-equivalents)
 *    should use this form to keep zero-cost-when-off.
 *
 * 3. **NEVER throws to business logic.** Every helper wraps the
 *    Faro SDK call AND the closure invocation in try/catch. Any
 *    failure is silently dropped. Observability emission MUST NOT
 *    impact business logic.
 *
 * 4. **Captured ONCE at module init** from the build-time-inlined
 *    env var. Vite replaces `import.meta.env.VITE_FARO_DISABLED`
 *    with the literal value at build time, so when set to `true`
 *    the compiler dead-code-eliminates the entire emit body.
 */
let observabilityEnabled = import.meta.env.VITE_FARO_DISABLED !== 'true'

/**
 * Runtime override — flip the latch without rebuilding. Mainly for
 * tests; production code shouldn't need this (env-based decision
 * at build is the canonical path).
 */
export function setObservabilityEnabled(value: boolean): void {
  observabilityEnabled = value
}

export function isObservabilityEnabled(): boolean {
  return observabilityEnabled
}

/**
 * Whether Faro is actually initialized + ready. Returns false when
 * `VITE_FARO_DISABLED=true` skipped init, OR when init failed
 * silently. Every helper below combines this with the latch check.
 */
function faroReady(): boolean {
  return typeof faro?.api?.pushLog === 'function'
}

/**
 * Resolve attribute arg — invoke closure if needed. Wrapped in
 * try/catch so a buggy producer can't bubble up to the caller.
 * Returns empty dict on producer failure.
 */
function resolveAttrs(attrs: AttrsArg): FaroAttributes {
  if (typeof attrs !== 'function') {
    return attrs
  }
  try {
    return attrs()
  } catch {
    // Producer failed; emit an empty payload so we don't lose the
    // event name entirely. Won't crash the caller.
    return {}
  }
}

/**
 * Emit a structured event log line. Surfaces in Loki under
 * `{app="lockerserver-fe"}` with `event=<name>` parsable via
 * `| json | event="auth.login.success"`.
 *
 * Use for: business-meaningful occurrences (login attempt, request
 * retry, CSRF refresh). NOT for general debug output — that's
 * `console.log` (which Faro doesn't capture by config).
 */
export function emitEvent(name: FaroEventName, attributes: AttrsArg = {}): void {
  if (!observabilityEnabled) return
  if (!faroReady()) return
  try {
    faro.api.pushEvent(name, coerceAttrs(resolveAttrs(attributes)))
  } catch {
    // Same hard-contract as the BE Metrics facade: observability
    // emission MUST NOT impact business logic. Drop silently on any
    // SDK-internal failure.
  }
}

/**
 * Emit a log line at WARN level. Use for non-failure but
 * actionable signals — request retried, soft validation hit,
 * fallback path taken.
 */
export function emitWarning(message: string, attributes: AttrsArg = {}): void {
  if (!observabilityEnabled) return
  if (!faroReady()) return
  try {
    faro.api.pushLog([message], {
      level: LogLevel.WARN,
      context: coerceAttrs(resolveAttrs(attributes)),
    })
  } catch {
    // intentional swallow — see emitEvent kdoc
  }
}

/**
 * Push an Error to Faro. The SDK extracts the stack frame, message,
 * and any cause chain. Use for caught exceptions where you want the
 * stack trace preserved — network failures that exhausted retries,
 * unexpected serialization errors, anything the user would want to
 * debug from a stack.
 *
 * Always passes through the original `error` so Faro sees the real
 * stack; don't wrap with `new Error(...)` because that resets the
 * trace to this call site.
 */
export function emitError(error: unknown, attributes: AttrsArg = {}): void {
  if (!observabilityEnabled) return
  if (!faroReady()) return
  try {
    const err = error instanceof Error ? error : new Error(String(error))
    faro.api.pushError(err, { context: coerceAttrs(resolveAttrs(attributes)) })
  } catch {
    // intentional swallow
  }
}

/**
 * Push a numeric measurement. Faro forwards these to its
 * measurements pipeline; LogQL can aggregate via `unwrap` on the
 * Loki side. Use for histograms / gauges (request duration, queue
 * depth, page-load timing).
 */
export function emitMeasurement(
  type: string,
  values: Record<string, number> | (() => Record<string, number>),
  attributes: AttrsArg = {},
): void {
  if (!observabilityEnabled) return
  if (!faroReady()) return
  try {
    const resolvedValues = typeof values === 'function' ? values() : values
    faro.api.pushMeasurement(
      { type, values: resolvedValues },
      { context: coerceAttrs(resolveAttrs(attributes)) },
    )
  } catch {
    // intentional swallow
  }
}

/**
 * Faro requires string-valued attributes; coerce numbers + booleans
 * at the boundary so call sites can pass the natural type.
 */
function coerceAttrs(attrs: FaroAttributes): Record<string, string> {
  const out: Record<string, string> = {}
  for (const [key, value] of Object.entries(attrs)) {
    out[key] = typeof value === 'string' ? value : String(value)
  }
  return out
}

/**
 * Convenience derivation of the anonymous-vs-authed flag from a
 * `User | null` value. Many event call sites want to tag with this;
 * centralize the predicate so it stays consistent + doesn't
 * accidentally include user_id (which would be unbounded
 * cardinality on the wire).
 *
 * Returns `'true'` / `'false'` strings because Faro string-coerces
 * attribute values anyway — keeps the wire shape predictable.
 *
 * Callers pass `globalStore.user.detail` directly; this helper does
 * NOT import the store to avoid a circular at module init
 * (events.ts is consumed by useAPI; useAPI is consumed by stores).
 */
export function anonymousFlagFor(user: unknown): string {
  return user === null || user === undefined ? 'true' : 'false'
}
